// w38 Marigold — autumn-marigold watercolor wedding preview card.
//
// The full template is a cream + terracotta + sage invitation with a
// flowering arch, marigold garland and a circular medallion. The catalog
// card keeps that palette: a warm cream ground inside a terracotta
// paper-fold frame, a round marigold medallion (layered petals) over the
// couple's names, eyebrow + footer in the standard small-caps treatment.
import { type LayoutCtx } from "./_shared";

const FRAME =
  "linear-gradient(135deg, #f2d9b8 0%, #d98b4e 22%, #f2d9b8 48%, #d98b4e 74%, #f2d9b8 100%)";

export function MarigoldPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const sage = "#8fa073";
  const marigold = "#e2953f";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: accent,
            marginBottom: "6%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Round marigold medallion. */}
        <div style={{ width: "62%", aspectRatio: "1 / 1", position: "relative", marginBottom: "6%" }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: FRAME, padding: "5%" }}>
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                boxSizing: "border-box",
              }}
            >
              <svg viewBox="0 0 100 100" width="72%" height="72%" aria-hidden>
                {/* leaves */}
                <g fill={sage} opacity="0.75">
                  <ellipse cx="26" cy="60" rx="9" ry="4" transform="rotate(-35 26 60)" />
                  <ellipse cx="74" cy="60" rx="9" ry="4" transform="rotate(35 74 60)" />
                  <ellipse cx="50" cy="80" rx="9" ry="4" />
                </g>
                {/* marigold petals — two rings */}
                {Array.from({ length: 12 }).map((_, i) => {
                  const a = (i / 12) * Math.PI * 2;
                  return (
                    <ellipse
                      key={`o${i}`}
                      cx={50 + Math.cos(a) * 20}
                      cy={44 + Math.sin(a) * 20}
                      rx="8"
                      ry="4.5"
                      fill={marigold}
                      opacity="0.9"
                      transform={`rotate(${(a * 180) / Math.PI} ${50 + Math.cos(a) * 20} ${44 + Math.sin(a) * 20})`}
                    />
                  );
                })}
                {Array.from({ length: 8 }).map((_, i) => {
                  const a = (i / 8) * Math.PI * 2 + 0.4;
                  return (
                    <ellipse
                      key={`i${i}`}
                      cx={50 + Math.cos(a) * 11}
                      cy={44 + Math.sin(a) * 11}
                      rx="6"
                      ry="3.6"
                      fill={accent}
                      transform={`rotate(${(a * 180) / Math.PI} ${50 + Math.cos(a) * 11} ${44 + Math.sin(a) * 11})`}
                    />
                  );
                })}
                <circle cx="50" cy="44" r="6" fill="#8c491a" />
              </svg>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 30, lineHeight: 1.05, color: ink }}>{names}</div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: accent,
            marginTop: "4%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
